import React from 'react';
import { compose } from 'redux';
import type { HolocronConfigObject } from 'holocron';
import { useSelector } from 'react-redux';
import {
  useCredoRampup,
  useDismissibleIguazu,
  RAMPED_UP_VARIANT,
  ALLOW_LIST_VARIANT,
} from '@americanexpress/customer-dashboard-utils';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import ConsumerNavigation from './ConsumerNavigation';
import { getIsSupportedMarket } from '../selectors';
import reducer from '../ducks';
import loadModuleData from '../ducks/loadModuleData';
import useModuleData from '../hooks/useModuleData';
import { NAV_TOOLTIP_DISMISSED } from '../constants';

export type ContainerType = React.FC<ConsumerNavigationPropsWrapped> & {
  holocron: HolocronConfigObject<ConsumerNavigationProps>;
};

const Container: ContainerType = (props) => {
  const {
    view: propView,
    locale,
    containerClassName: propContainerClassName,
    children,
    route: {
      view: routeView,
      containerClassName: routeContainerClassName,
    } = {},
  } = props;

  const view = propView || routeView || 'PRODUCT';
  const containerClassName = propContainerClassName || routeContainerClassName;

  const {
    data: { isEligible, variantType },
    error: eligibilityError,
  } = useCredoRampup();

  const isSupportedMarket = useSelector(getIsSupportedMarket);

  const isConsumerNavEligible = isEligible
    && (variantType === RAMPED_UP_VARIANT || variantType === ALLOW_LIST_VARIANT)
    && !eligibilityError
    && isSupportedMarket;

  const {
    loading,
    error,
    data: {
      lang,
      links,
      ConsumerContextSwitcher,
      SiteAreaNavContainer,
      MycaSiteAreaNav,
    },
  } = useModuleData({
    ...props,
    view,
  }, {
    isConsumerNavEligible,
  });

  const {
    isLoading: isTooltipLoading,
    isDismissed: isTooltipDismissed,
    dismiss: dismissTooltip,
  } = useDismissibleIguazu({ preferenceId: NAV_TOOLTIP_DISMISSED });

  const isContextSwitcherLoading = loading?.ConsumerContextSwitcher;
  const isSANContainerLoading = loading?.lang || loading?.SiteAreaNavContainer;

  const hasSanError = !!(error?.lang || error?.links || error?.SiteAreaNavContainer);

  return (
    <section data-module-name="axp-consumer-navigation">
      {!isConsumerNavEligible ? (
        <>
          {MycaSiteAreaNav && <MycaSiteAreaNav />}
          <div
            data-testid="page_content_container"
            className={containerClassName}
          >
            {children}
          </div>
        </>
      ) : (
        <ConsumerNavigation
          view={view}
          containerClassName={containerClassName}
          locale={locale}
          isContextSwitcherLoading={isContextSwitcherLoading}
          isSANContainerLoading={isSANContainerLoading}
          hasSanError={hasSanError}
          lang={lang}
          links={links}
          ConsumerContextSwitcher={ConsumerContextSwitcher}
          SiteAreaNavContainer={SiteAreaNavContainer}
          isTooltipLoading={isTooltipLoading}
          isTooltipDismissed={isTooltipDismissed}
          dismissTooltip={dismissTooltip}
        >
          {children}
        </ConsumerNavigation>
      )}
    </section>
  );
};

export const TestableContainer = Container;

Container.holocron = {
  name: 'axp-consumer-navigation',
  loadModuleData,
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- required
  // @ts-ignore-next-line
  reducer,
  options: {
    provideModuleState: false,
  },
};

const hocChain = compose(
  oneAppModuleWrapper(
    'axp-consumer-navigation'
  )
);

export default hocChain(Container);
